<template>
	<div
		:aria-labelledby="`${groupId}-label`"
		class="amelipro-filters"
		:role="unique ? 'radiogroup' : 'group'"
	>
		<p
			:id="`${groupId}-label`"
			class="mb-1 ap-grey--text text--darken-1 amelipro-filters__label"
			:class="hiddenLabel ? 'd-sr-only' : undefined"
		>
			{{ groupLabel }}
		</p>

		<div
			class="d-flex flex-sm-wrap flex-column flex-sm-row justify-center justify-sm-start align-center amelipro-filters__group"
		>
			<label
				v-for="(item, index) in items"
				:key="index"
				class="mb-2 d-block font-weight-semibold amelipro-filters__item"
				:class="breakpointHelper.isDesktop() ? 'item-spacing' : undefined"
			>
				<input
					:id="item.id"
					v-model="currentlySelected"
					:checked="!props.unique && item.isChecked ? true : undefined"
					class="amelipro-filters__filter__input"
					:name="groupId"
					:type="unique ? 'radio' : 'checkbox'"
					:value="item.value"
					@change="emitValue(item)"
				>

				<span
					class="filter d-block amelipro-filters__filter__label"
				>
					{{ item.label }}
				</span>
			</label>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { PropType, ref, watch } from 'vue';
	import { AmeliproFilterItem } from './types';
	import BreakpointHelper from '../../helpers/BreakpointHelper';
	import { IndexedObject } from '../../types';

	const props = defineProps({
		groupId: {
			type: String,
			required: true,
		},
		groupLabel: {
			type: String,
			required: true,
		},
		hiddenLabel: {
			type: Boolean,
			default: false,
		},
		items: {
			type: Array as PropType<AmeliproFilterItem[]>,
			default: () => [],
		},
		unique: {
			type: Boolean,
			default: false,
		},
	});

	const breakpointHelper = new BreakpointHelper('smAndUp');

	const selectedItems = ref<IndexedObject<string>[]>([]);
	const selectedItem = ref<string>('');
	const currentlySelected = ref<typeof selectedItem | typeof selectedItems>();
	const updateSelectedItems = () => {
		selectedItems.value = [];
		props.items.forEach((element) => {
			if (element.isChecked) {
				selectedItems.value.push(element.value as unknown as IndexedObject<string>);
			}
		});
	};
	const updateSelectedItem = () => {
		selectedItem.value = (props.items.find((element) => element.isChecked))?.value || '';
	};
	// NB: quand unique === true, selectedItems devient un String !
	const updateSelection = () => {
		if (props.unique) {
			currentlySelected.value = selectedItem;
			updateSelectedItem();
		} else {
			currentlySelected.value = selectedItems;
			updateSelectedItems();
		}
	};
	watch(() => props.items, () => {
		updateSelection();
	}, { immediate: true });
	watch(() => props.unique, () => {
		updateSelection();
	}, { immediate: true });

	const emit = defineEmits(['input']);
	const emitValue = (item: AmeliproFilterItem): void => {
		item.isChecked = !item.isChecked;
		emit('input', currentlySelected.value);
	};
</script>

<style lang="scss" scoped>
.item-spacing {
	&:not(:last-child) {

		@media #{$media-up-sm} {
			margin-right: 16px;
		}
	}
}

input {
	position: absolute;
	width: 0;
	height: 0;
	opacity: 0;
}

.filter {
	padding: $filter-padding-y $filter-padding-x;
	font-weight: $filter-font-weight;

	input+& {
		position: relative;
		border-radius: $filter-radius;
		border: 1px solid $ap-blue-darken1;
		background-color: $ap-white;
		color: $ap-blue-darken1;
		cursor: pointer;
	}

	input:checked+& {
		background-color: $ap-blue-lighten3;

		&::after {
			position: absolute;
			top: 1.1rem;
			left: 1.1rem;
			width: 1rem;
			height: 0.5rem;
			border-bottom: 2px solid $ap-blue-darken1;
			border-left: 2px solid $ap-blue-darken1;
			transform: rotate(-50deg);
			content: "";
		}
	}

	input:focus+& {
		outline: 1px dotted $ap-grey-darken1;
	}

	input:disabled+& {
		cursor: default;
	}
}

.amelipro-filters__label {
	font-weight: $label-font-weight;
}
</style>
